import { ImportOutlined, PlusOutlined } from '@ant-design/icons';
import { useContext } from 'react';
import LogoMark from '../../assets/images/logo/favicon.svg?react';
import { ModalContext, ModalType } from '../../context/ModalContext';
import { TaskType } from '../../types/TaskType';
import { WorkspaceViewEntityType, WorkspaceViewGroup } from '../../types/WorkspaceView';
import { BaseButton } from './BaseButton';

interface TasksZeroStateProps {
  onImportClick?: () => void;
  viewGroup?: WorkspaceViewGroup;
  entityType?: WorkspaceViewEntityType;
  // Overrides the computed title (e.g. for reuse outside the task list, like the inbox empty state).
  title?: string;
  // Hides the "Create your first Task"/import CTA entirely, regardless of viewGroup/entityType.
  hideCta?: boolean;
  // Set false to size to the parent container instead of the task-list full-page height.
  fullHeight?: boolean;
}

export const TasksZeroState = ({
  onImportClick,
  viewGroup,
  entityType,
  title: titleOverride,
  hideCta = false,
  fullHeight = true,
}: TasksZeroStateProps) => {
  const { openModal } = useContext(ModalContext);

  const isEpic = entityType === WorkspaceViewEntityType.epic;

  const handleCreateTask = () => {
    openModal(ModalType.CreateItem, {
      taskType: isEpic ? TaskType.epic : TaskType.task,
    });
  };

  const isArchive = viewGroup === WorkspaceViewGroup.archive;
  const isTriage = viewGroup === WorkspaceViewGroup.triage;
  const showImport =
    !hideCta && !isArchive && !isTriage && !isEpic && import.meta.env.VITE_FEATURE_IMPORT === 'true' && onImportClick;

  const title =
    titleOverride ??
    (isArchive
      ? 'No archived tasks'
      : isTriage
        ? 'Nothing to triage'
        : isEpic
          ? 'There are no Epics yet'
          : 'There are no Tasks yet');

  return (
    <div
      style={fullHeight ? { height: 'calc(100vh - 178px)' } : undefined}
      className="flex flex-col items-center justify-center gap-5 h-full w-full"
    >
      <div className="flex items-center justify-center w-[46px] h-[46px] bg-neutral-100 rounded-full">
        <LogoMark className="w-6 h-6" />
      </div>

      <div className="text-neutral-900 text-sm font-medium">{title}</div>

      {!isArchive && !hideCta && (
        <BaseButton size="large" type="accent" className="rounded-lg" onClick={handleCreateTask}>
          <PlusOutlined />
          {isTriage ? 'Create a task' : isEpic ? 'Create your first Epic' : 'Create your first Task'}
        </BaseButton>
      )}

      {showImport && (
        <>
          <div className="flex items-center gap-3 w-[200px]">
            <div className="flex-1 h-px bg-neutral-300" />
            <span className="text-neutral-500 text-xs">or</span>
            <div className="flex-1 h-px bg-neutral-300" />
          </div>

          <BaseButton size="medium" type="secondary" onClick={onImportClick}>
            <span className="flex items-center gap-2">
              <ImportOutlined />
              Import from CSV
            </span>
          </BaseButton>
        </>
      )}
    </div>
  );
};
